import _ from 'underscore';
import {
    fetchFromApi,
    prefetchFromApi,
    getCachedResponse,
    formatQuerystring,
    optionalUrlSegment,
    AUCTIONEERS_PER_PAGE,
    DEFAULT_AUCTIONS_PER_PAGE
} from '../utils/gateways';
import {
    getFeature,
    FEATURE__REFINE,
    FEATURE__SORT_AUCTIONEERS
} from '../utils/features';

// auctions

// Build the endpoint URL for filtered auctions (shared by fetch and prefetch)
var buildAuctionsEndpoint = function(filters, page, pageSize) {
    var state = filters && filters.state;
    var association = filters && filters.association;
    var categories = (filters && filters.categories) || [];
    var premium = filters && filters.premium;
    var past = filters && filters.past;
    var categoriesStr = _.compact(categories).join(',');

    var shouldUseRefine = getFeature(FEATURE__REFINE);

    if (shouldUseRefine) {
        var queryString = formatQuerystring({
            state: state,
            association: association,
            page: page,
            items: pageSize,
            categories: categoriesStr,
            premium: premium,
            past: past,
        });

        return { endpoint: 'auctions/refine/' + queryString, useRefine: true };
    }

    if (categoriesStr) {
        var querystring = formatQuerystring({
            state: state,
            page: page,
            items: pageSize,
            associations: association
        });

        return { endpoint: 'auctions/category/' + categories[0] + querystring, useRefine: false };
    } else if (association) {
        var pagination = formatQuerystring({
            page: page,
            items: pageSize,
        });

        return { endpoint: 'auctions/association/' + association + pagination, useRefine: false };
    } else {
        var paginationQs = formatQuerystring({
            page: page,
            items: pageSize,
        });

        return { endpoint: 'auctions/' + optionalUrlSegment(state) + paginationQs, useRefine: false };
    }
};

export var fetchFilteredAuctions = function(params) {
    var filters = params && params.filters;
    var page = params && params.page;
    var pageSize = params && params.pageSize;
    var info = buildAuctionsEndpoint(filters, page, pageSize);

    if (info.useRefine) {
        return fetchFromApi(info.endpoint)
            .then(function(result) {
                // Return normalized structure with pagination info
                return {
                    items: result.Items || [],
                    totalPages: result.TotalPages,
                    totalItems: result.TotalItems,
                    currentPage: result.CurrentPage
                };
            });
    }

    // Legacy endpoints return plain array - wrap in normalized structure
    return fetchFromApi(info.endpoint)
        .then(function(result) {
            var items = Array.isArray(result) ? result : [];
            return {
                items: items,
                totalPages: null,  // Legacy doesn't provide this
                totalItems: null,
                currentPage: null
            };
        });
};

// Prefetch auctions page in background (for pagination)
export var prefetchFilteredAuctions = function(params) {
    var filters = params && params.filters;
    var page = params && params.page;
    var pageSize = params && params.pageSize;
    var info = buildAuctionsEndpoint(filters, page, pageSize);
    prefetchFromApi(info.endpoint);
};

// Check if auctions are cached (for skipping loading state)
export var getCachedFilteredAuctions = function(params) {
    var filters = params && params.filters;
    var page = params && params.page;
    var pageSize = params && params.pageSize;
    var info = buildAuctionsEndpoint(filters, page, pageSize);
    var cached = getCachedResponse(info.endpoint);

    if (!cached) return null;

    if (info.useRefine) {
        // Return normalized structure with pagination info
        return {
            items: cached.Items || [],
            totalPages: cached.TotalPages,
            totalItems: cached.TotalItems,
            currentPage: cached.CurrentPage
        };
    }

    // Legacy endpoints return plain array - wrap in normalized structure
    var items = Array.isArray(cached) ? cached : [];
    return {
        items: items,
        totalPages: null,
        totalItems: null,
        currentPage: null
    };
};


export var fetchAuction = function(auctionId) {
    return fetchFromApi('auction/' + auctionId);
};

// Check if auction is cached
export var getCachedAuction = function(auctionId) {
    return getCachedResponse('auction/' + auctionId);
};

// Prefetch auction data and main image in background
export var prefetchAuction = function(auctionId) {
    var endpoint = 'auction/' + auctionId;
    prefetchFromApi(endpoint);
};

export const fetchAuctionsForUser = (userId) => fetchFromApi(
    `auctions/user/${userId}`
);


// auctioneers

// Build the endpoint URL for auctioneers (shared by fetch and prefetch)
var buildAuctioneersEndpoint = function(filters, page, pageSize) {
    var association = filters && filters.association;
    var expertise = filters && filters.expertise;
    var search = filters && filters.search;
    var sort = filters && filters.sort;
    var sortAuctioneers = getFeature(FEATURE__SORT_AUCTIONEERS);

    if (sortAuctioneers) {
        sort = 'asc';
    }

    var queryString = formatQuerystring({
        association: association,
        expertise: expertise,
        page: page,
        name: search,
        items: pageSize || AUCTIONEERS_PER_PAGE,
        sort: sort,
    });

    return 'auctioneers/' + queryString;
};

export var fetchAuctioneers = function(filters, page, pageSize) {
    pageSize = pageSize || AUCTIONEERS_PER_PAGE;
    var endpoint = buildAuctioneersEndpoint(filters, page, pageSize);
    return fetchFromApi(endpoint);
};

// Prefetch auctioneers page in background (for pagination)
export var prefetchAuctioneers = function(filters, page, pageSize) {
    pageSize = pageSize || AUCTIONEERS_PER_PAGE;
    var endpoint = buildAuctioneersEndpoint(filters, page, pageSize);
    prefetchFromApi(endpoint);
};

// Check if auctioneers are cached (for skipping loading state)
export var getCachedAuctioneers = function(filters, page, pageSize) {
    pageSize = pageSize || AUCTIONEERS_PER_PAGE;
    var endpoint = buildAuctioneersEndpoint(filters, page, pageSize);
    return getCachedResponse(endpoint);
};


export const fetchAuctioneer = (auctioneerId) => fetchFromApi(
    `users/${auctioneerId}`
);

// Prefetch auctioneer profile in background
export var prefetchAuctioneer = function(auctioneerId) {
    prefetchFromApi('users/' + auctioneerId);
};

// Prefetch auctions for a specific user in background
export var prefetchAuctionsForUser = function(userId) {
    prefetchFromApi('auctions/user/' + userId);
};

// categories

export const fetchCategories = () => fetchFromApi('categories');

export const fetchExpertise = () => fetchFromApi('expertise');
